import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import serviceManager from '../services/serviceManager';

/**
 * Hook to track market data loading state
 */
export const useMarketDataLoading = () => {
  const { user } = useAuth();
  const [isMarketDataLoaded, setIsMarketDataLoaded] = useState(false);
  const [marketDataCount, setMarketDataCount] = useState(0);

  useEffect(() => {
    if (!user) {
      // If no user, mark market data as loaded (it doesn't need to load)
      setIsMarketDataLoaded(true);
      setMarketDataCount(0);
      return;
    }
    
    if (!serviceManager.areServicesRunning()) {
      setIsMarketDataLoaded(false);
      setMarketDataCount(0);
      return;
    }

    // Check for market data periodically
    const checkMarketData = () => {
      // Import binanceService dynamically to avoid circular dependencies
      import('../services/binanceService').then(({ default: binanceService }) => {
        const marketData = binanceService.getMarketData();
        const count = marketData.size;
        
        setMarketDataCount(count);
        
        // Consider market data loaded if we have at least 10 trading pairs
        if (count >= 10) {
          setIsMarketDataLoaded(true);
        }
      }).catch(() => {
        // If service is not available, assume loaded after a delay
        setTimeout(() => setIsMarketDataLoaded(true), 2000);
      });
    };

    // Check immediately
    checkMarketData();

    // Check periodically
    const interval = setInterval(checkMarketData, 500);

    // Fallback timeout
    const timeout = setTimeout(() => {
      setIsMarketDataLoaded(true);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [user]);

  return {
    isMarketDataLoaded,
    marketDataCount,
  };
};
